import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * Decorative background video: autoplay muted, or leave the sibling poster when blocked.
 * With a poster, video stays invisible until it is actually playing — no ATF black flash.
 * Sources stay on `data-video-source` until the component is ≥15% on screen so below-fold
 * hero / gallery clips (e.g. stacked mobile hero slot 2) do not compete with LCP.
 * `rootMargin` is 0 — a 200px pre-fetch still downloaded stacked mobile slot 2 on first paint.
 * IntersectionObserver can fire before the hero finishes layout, so we also re-check on
 * rAF and ResizeObserver (desktop ATF right-half must still start without scrolling).
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} [videoSources]
 * @property {HTMLVideoElement} [videoElement]
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {HTMLVideoElement | null} */
  #video = null;

  /** @type {boolean} */
  #activated = false;

  connectedCallback() {
    super.connectedCallback();

    const video = this.refs.videoElement ?? this.querySelector('video');
    if (!(video instanceof HTMLVideoElement)) return;

    this.#video = video;
    this.#setupReady(video);

    if (prefersReducedMotion()) return;

    if (typeof IntersectionObserver === 'undefined') {
      this.#activate(video);
      return;
    }

    this.#observer = new IntersectionObserver(
      () => {
        this.#tryActivate();
      },
      { root: null, rootMargin: '0px', threshold: [0, 0.15, 0.5, 1] }
    );

    this.#observer.observe(this);

    if (typeof ResizeObserver !== 'undefined') {
      this.#resizeObserver = new ResizeObserver(() => {
        this.#tryActivate();
      });
      this.#resizeObserver.observe(this);
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.#tryActivate());
    });
  }

  disconnectedCallback() {
    this.#teardownObservers();
    this.#video = null;
    super.disconnectedCallback();
  }

  #teardownObservers() {
    this.#observer?.disconnect();
    this.#observer = null;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
  }

  /**
   * True when enough of the component is actually on screen — not a 0px fold-edge hit
   * and not a 0-height first-paint before stacked hero rows resolve.
   *
   * @returns {boolean}
   */
  #isMostlyInView() {
    const rect = this.getBoundingClientRect();
    if (rect.height < 40) return false;

    const visible = Math.max(0, Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0));

    return visible / rect.height >= 0.15;
  }

  #tryActivate() {
    const video = this.#video;
    if (!video || this.#activated) return;
    if (!this.#isMostlyInView()) return;

    this.#activate(video);
    this.#teardownObservers();
  }

  /**
   * @param {HTMLVideoElement} video
   */
  #setupReady(video) {
    const hasPoster = [...this.children].some((el) => el instanceof HTMLImageElement);
    const reveal = () => this.setAttribute('data-video-ready', '');

    if (hasPoster) {
      if (!video.paused) reveal();
      else video.addEventListener('playing', reveal, { once: true });
    } else {
      reveal();
    }
  }

  /**
   * @param {HTMLVideoElement} video
   */
  #activate(video) {
    if (this.#activated) return;

    this.#activated = true;

    const sources = this.refs.videoSources?.length
      ? this.refs.videoSources
      : [...this.querySelectorAll('source[data-video-source]')];

    let attached = false;

    for (const source of sources) {
      if (!(source instanceof HTMLSourceElement)) continue;

      const url = source.dataset.videoSource;
      if (!url) continue;

      source.setAttribute('src', url);
      attached = true;
    }

    if (attached) video.load();

    const hasPoster = [...this.children].some((el) => el instanceof HTMLImageElement);

    // Property (not just attribute) is what autoplay policies check.
    video.muted = true;

    video.play().catch(() => {
      if (hasPoster) video.hidden = true;
    });
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
